import { PageGrid } from '../../../../components/PageGrid/PageGrid';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Link } from 'neon-design-system';
import { RequestStatusLabel } from '../../../../components/RequestStatusLabel/RequestStatusLabel';
import { AccountChangeRequest } from '../../../../types/request';
import { baseUrls } from '../../../../constants/urls';
import { sumBy } from 'lodash';
import { get } from 'lodash/fp';
import { pluralise } from '../../../../utils/entity';
import { localDateFormat } from '../../../../utils/date';
import { ChangeArrowCell } from '../../../../components/ChangeArrowCell/ChangeArrowCell';
import { usePaginationRequestInfo } from '../../../../providers/PaginationRequestProvider';
import { activeFilterName } from '../../pages/AccountChangeRequests/AccountChangeRequests';

interface Props {
  rowData: AccountChangeRequest[];
  paginationPageSize?: number;
  currentPage?: number;
}

export const pageName = 'account-change-requests';

export const AccountChangeRequestsGrid = ({ rowData, paginationPageSize, currentPage }: Props) => {
  const columnDefs: ColDef[] = [
    {
      field: 'requestRef',
      headerName: 'Request Ref.',
      sortable: true,
      cellRendererFramework: (p: ICellRendererParams) => (
        <Link to={`${baseUrls.accountChangeRequests}/${p.value}`}>
          <b>{p.value}</b>
        </Link>
      )
    },
    {
      field: 'positions',
      headerName: 'Trades',
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const trades = nodeA.data.positions.length > nodeB.data.positions.length;
        return trades ? 1 : -1;
      },
      valueFormatter: ({ value }) => {
        const tradesLength = value.length;
        const totalQuantity = sumBy(value, get('quantity'));
        return `${value.length} ${pluralise(tradesLength, 'trade')} for a total of ${totalQuantity} ${pluralise(totalQuantity, 'lot')}`;
      }
    },
    {
      field: 'requestor',
      headerName: 'Requestor',
      valueFormatter: ({ value }): string => {
        const { firstName, lastName } = value || {};

        return Boolean(firstName) && Boolean(lastName) ? `${firstName} ${lastName}` : '';
      }
    },
    {
      field: 'sourceAccount',
      headerName: 'Accounts',
      sortable: true,
      cellRendererFramework: (p: ICellRendererParams) => {
        return <ChangeArrowCell oldValue={p?.data?.sourceAccount} newValue={p?.data?.destinationAccount} />;
      }
    },
    {
      field: 'requestDate',
      headerName: 'Request Date',
      sortable: true,
      valueFormatter: ({ value }) => {
        return localDateFormat(new Date(value), true);
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      cellRendererFramework: (p: ICellRendererParams) => {
        return (
          <div>
            <RequestStatusLabel status={p.data.status} />
          </div>
        );
      }
    }
  ];

  const { onSortChanged } = usePaginationRequestInfo(activeFilterName);

  return (
    <PageGrid
      onSortChanged={onSortChanged}
      cols={columnDefs}
      rowData={rowData}
      paginationPageSize={paginationPageSize}
      currentPage={currentPage}
      pageName={pageName}
    />
  );
};
